import { PageLoader } from '@/components/ui/loader';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarDays, Copy, ExternalLink, Link2, Plus, Trash2, Users } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Switch, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { MeetingStatus } from './record-tools';
import {
  recordPath,
  useDeleteMeetingLink,
  useMeetingLinks,
  useMeetings,
  useSaveMeetingLink,
  useUpdateMeeting,
  type Availability,
  type Meeting,
  type MeetingLink,
  type Weekday,
} from './api';

const DAYS: [Weekday, string][] = [
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday'],
  ['sun', 'Sunday'],
];

const DEFAULT_AVAILABILITY: Availability = {
  mon: [['09:00', '18:00']],
  tue: [['09:00', '18:00']],
  wed: [['09:00', '18:00']],
  thu: [['09:00', '18:00']],
  fri: [['09:00', '18:00']],
};

const LOCATION_LABELS: Record<MeetingLink['locationType'], string> = {
  video: 'Video call',
  phone: 'Phone call',
  in_person: 'In person',
  custom: 'Other',
};

const time = (value: string) =>
  new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(value));

/**
 * Meetings (HubSpot's meetings tool): what is coming up, what happened, and
 * the booking pages customers use to pick a time themselves.
 */
export function MeetingsPage() {
  const [range, setRange] = useState<'upcoming' | 'past'>('upcoming');
  const now = useMemo(() => new Date().toISOString(), []);
  const { data: meetings = [], isLoading } = useMeetings(range === 'upcoming' ? { from: now } : { to: now });
  const { data: links = [] } = useMeetingLinks();
  const [editing, setEditing] = useState<MeetingLink | 'new' | null>(null);
  const [outcomeFor, setOutcomeFor] = useState<Meeting | null>(null);

  const list = range === 'past' ? [...meetings].reverse() : meetings;
  const byDay = useMemo(() => {
    const groups = new Map<string, Meeting[]>();
    for (const meeting of list) {
      const key = formatDate(meeting.startsAt, 'EEEE d MMMM');
      groups.set(key, [...(groups.get(key) ?? []), meeting]);
    }
    return [...groups.entries()];
  }, [list]);

  const copy = async (url: string) => {
    await navigator.clipboard.writeText(url).catch(() => undefined);
    toast.success('Link copied');
  };

  return (
    <div>
      <PageHeader
        title="Meetings"
        description="Your calendar of customer meetings, and booking pages that let customers choose a free slot — round robin across a team included."
        breadcrumbs={[{ label: 'Sales hub' }, { label: 'Meetings' }]}
        actions={
          <Can permission="crm.meeting.create">
            <Button onClick={() => setEditing('new')}>
              <Plus /> New booking page
            </Button>
          </Can>
        }
      />

      <div className="grid gap-5 xl:grid-cols-[1fr_24rem]">
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="h-4 w-4" /> {range === 'upcoming' ? 'Coming up' : 'Past meetings'}
            </CardTitle>
            <div className="flex rounded-lg border p-0.5 text-xs">
              {(['upcoming', 'past'] as const).map((value) => (
                <button
                  key={value}
                  onClick={() => setRange(value)}
                  className={cn('rounded-md px-3 py-1', range === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}
                >
                  {value === 'upcoming' ? 'Upcoming' : 'Past'}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <PageLoader />
            ) : byDay.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {range === 'upcoming' ? 'Nothing booked yet. Share a booking page to fill your calendar.' : 'No past meetings.'}
              </p>
            ) : (
              <div className="space-y-5">
                {byDay.map(([day, items]) => (
                  <div key={day}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{day}</p>
                    <div className="space-y-2">
                      {items.map((meeting) => {
                        const path = recordPath(meeting.record?.type, meeting.record?.id);
                        return (
                          <div key={meeting.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="w-20 shrink-0 text-sm font-semibold text-primary">
                                {time(meeting.startsAt)}
                                <span className="block text-xs font-normal text-muted-foreground">to {time(meeting.endsAt)}</span>
                              </div>
                              <div className="min-w-0">
                                <p className="truncate font-medium">{meeting.title}</p>
                                <p className="truncate text-xs text-muted-foreground">
                                  {meeting.host?.name}
                                  {meeting.guestEmail ? ` · ${meeting.guestEmail}` : ''}
                                  {meeting.guestPhone ? ` · ${meeting.guestPhone}` : ''}
                                  {meeting.location ? ` · ${meeting.location}` : ''}
                                </p>
                                {path && meeting.record && (
                                  <Link to={path} className="text-xs text-link hover:underline">
                                    {meeting.record.name}
                                  </Link>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <MeetingStatus status={meeting.status} />
                              {meeting.status === 'SCHEDULED' && (
                                <Can permission="crm.meeting.update">
                                  <Button size="sm" variant="outline" onClick={() => setOutcomeFor(meeting)}>
                                    {range === 'past' ? 'Log outcome' : 'Update'}
                                  </Button>
                                </Can>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-3">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <Link2 className="h-4 w-4" /> Booking pages
          </p>
          {links.length === 0 && (
            <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
              Make your first booking page — customers see your free times and pick one. It lands in this calendar and on
              their record.
            </div>
          )}
          {links.map((link) => (
            <Card key={link.id}>
              <CardContent className="space-y-2 pt-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{link.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {link.durationMinutes} min · {LOCATION_LABELS[link.locationType]} · {link.bookings} booked
                    </p>
                  </div>
                  <Badge className={link.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}>
                    {link.isActive ? 'Live' : 'Off'}
                  </Badge>
                </div>
                {link.kind === 'ROUND_ROBIN' && (
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="h-3 w-3" /> Round robin: {link.members.map((member) => member.name).join(', ')}
                  </p>
                )}
                <p className="truncate rounded-md bg-muted/50 px-2 py-1 font-mono text-xs">{link.url}</p>
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="outline" onClick={() => copy(link.url)}>
                    <Copy /> Copy
                  </Button>
                  <Button size="sm" variant="ghost" asChild>
                    <a href={link.url} target="_blank" rel="noreferrer">
                      <ExternalLink /> Open
                    </a>
                  </Button>
                  {link.canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => setEditing(link)}>
                      Edit
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {editing && <LinkEditor link={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {outcomeFor && <OutcomeDialog meeting={outcomeFor} onClose={() => setOutcomeFor(null)} />}
    </div>
  );
}

function OutcomeDialog({ meeting, onClose }: { meeting: Meeting; onClose: () => void }) {
  const update = useUpdateMeeting();
  const past = new Date(meeting.endsAt).getTime() < Date.now();
  const [status, setStatus] = useState<Meeting['status']>(past ? 'COMPLETED' : 'SCHEDULED');
  const [outcome, setOutcome] = useState(meeting.outcome ?? '');
  const [notes, setNotes] = useState(meeting.notes ?? '');

  const submit = () =>
    update.mutate(
      { id: meeting.id, status, outcome: outcome || null, notes: notes || null },
      {
        onSuccess: () => {
          toast.success(status === 'CANCELLED' ? 'Meeting cancelled — the guest has been told' : 'Saved');
          onClose();
        },
        onError: (error) => toast.error(error instanceof ApiRequestError ? error.message : 'Could not save'),
      },
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{meeting.title}</DialogTitle>
          <DialogDescription>{formatDate(meeting.startsAt, 'EEEE d MMMM, HH:mm')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onChange={(event) => setStatus(event.target.value as Meeting['status'])}>
              <option value="SCHEDULED">Scheduled</option>
              <option value="COMPLETED">Held</option>
              <option value="NO_SHOW">They did not show</option>
              <option value="CANCELLED">Cancel it (tells the guest)</option>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Outcome</Label>
            <Input value={outcome} onChange={(event) => setOutcome(event.target.value)} placeholder="e.g. Proposal requested" />
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={submit} disabled={update.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkEditor({ link, onClose }: { link: MeetingLink | null; onClose: () => void }) {
  const save = useSaveMeetingLink();
  const remove = useDeleteMeetingLink();
  const { data: users = [] } = useUserOptions();
  const [form, setForm] = useState({
    name: link?.name ?? 'Discovery call',
    slug: link?.slug ?? '',
    description: link?.description ?? '',
    kind: link?.kind ?? ('PERSONAL' as MeetingLink['kind']),
    memberIds: link?.memberIds ?? ([] as string[]),
    durationMinutes: link?.durationMinutes ?? 30,
    locationType: link?.locationType ?? ('video' as MeetingLink['locationType']),
    location: link?.location ?? '',
    timezone: link?.timezone ?? 'Asia/Dubai',
    availability: link?.availability ?? DEFAULT_AVAILABILITY,
    bufferMinutes: link?.bufferMinutes ?? 10,
    minNoticeHours: link?.minNoticeHours ?? 4,
    maxDaysAhead: link?.maxDaysAhead ?? 30,
    questions: link?.questions ?? [{ id: 'q1', label: 'What would you like to discuss?', required: false }],
    createLead: link?.createLead ?? true,
    isActive: link?.isActive ?? true,
  });
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }));

  const setDay = (day: Weekday, windows: [string, string][] | undefined) =>
    set('availability', { ...form.availability, [day]: windows && windows.length ? windows : undefined });

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        id: link?.id,
        ...form,
        slug: form.slug.trim().toLowerCase(),
        description: form.description || null,
        location: form.location || null,
        questions: form.questions.filter((question) => question.label.trim()),
      });
      toast.success('Booking page saved');
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiRequestError ? failure.message : 'Could not save');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{link ? 'Edit booking page' : 'New booking page'}</DialogTitle>
          <DialogDescription>Customers see the free times inside these hours, minus anything already booked.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Meeting name</Label>
              <Input value={form.name} onChange={(event) => set('name', event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Address</Label>
              <div className="flex items-center rounded-lg border bg-card ps-2 text-sm">
                <span className="shrink-0 text-muted-foreground">/meet/</span>
                <input
                  className="h-10 min-w-0 flex-1 rounded-e-lg bg-transparent px-1 outline-none"
                  placeholder="made from the name"
                  value={form.slug}
                  onChange={(event) => set('slug', event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                />
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(event) => set('description', event.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Length</Label>
              <Select value={form.durationMinutes} onChange={(event) => set('durationMinutes', Number(event.target.value))}>
                {[15, 20, 30, 45, 60, 90].map((value) => (
                  <option key={value} value={value}>
                    {value} minutes
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Where</Label>
              <Select value={form.locationType} onChange={(event) => set('locationType', event.target.value as MeetingLink['locationType'])}>
                {Object.entries(LOCATION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{form.locationType === 'in_person' ? 'Address' : form.locationType === 'video' ? 'Meeting link' : 'Details'}</Label>
              <Input value={form.location} onChange={(event) => set('location', event.target.value)} placeholder={form.locationType === 'video' ? 'https://meet.google.com/…' : ''} />
            </div>
          </div>

          <div className="space-y-2 rounded-xl border p-3">
            <div className="flex flex-wrap items-center gap-3">
              <Label>Who takes these meetings</Label>
              <Select className="h-8 w-auto text-xs" value={form.kind} onChange={(event) => set('kind', event.target.value as MeetingLink['kind'])}>
                <option value="PERSONAL">Just me</option>
                <option value="ROUND_ROBIN">A team, in turn (round robin)</option>
              </Select>
            </div>
            {form.kind === 'ROUND_ROBIN' && (
              <div className="grid max-h-40 gap-1 overflow-y-auto sm:grid-cols-2">
                {users.map((user) => (
                  <label key={user.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.memberIds.includes(user.id)}
                      onCheckedChange={(value) =>
                        set('memberIds', value ? [...form.memberIds, user.id] : form.memberIds.filter((id) => id !== user.id))
                      }
                    />
                    {user.name}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2 rounded-xl border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>Working hours</Label>
              <Select className="h-8 w-auto text-xs" value={form.timezone} onChange={(event) => set('timezone', event.target.value)}>
                {['Asia/Dubai', 'Asia/Riyadh', 'Asia/Qatar', 'Asia/Kolkata', 'Asia/Karachi', 'Europe/London', 'America/New_York'].map((zone) => (
                  <option key={zone}>{zone}</option>
                ))}
              </Select>
            </div>
            {DAYS.map(([day, label]) => {
              const windows = form.availability[day];
              const open = Boolean(windows?.length);
              return (
                <div key={day} className="flex flex-wrap items-center gap-2 text-sm">
                  <label className="flex w-32 items-center gap-2">
                    <Switch checked={open} onCheckedChange={(value) => setDay(day, value ? [['09:00', '18:00']] : undefined)} />
                    {label}
                  </label>
                  {open ? (
                    windows!.map(([from, to], index) => (
                      <span key={index} className="flex items-center gap-1">
                        <Input
                          type="time"
                          className="h-8 w-28"
                          value={from}
                          onChange={(event) => setDay(day, windows!.map((window, at) => (at === index ? [event.target.value, window[1]] : window)))}
                        />
                        –
                        <Input
                          type="time"
                          className="h-8 w-28"
                          value={to}
                          onChange={(event) => setDay(day, windows!.map((window, at) => (at === index ? [window[0], event.target.value] : window)))}
                        />
                      </span>
                    ))
                  ) : (
                    <span className="text-muted-foreground">Closed</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Gap between meetings</Label>
              <Select value={form.bufferMinutes} onChange={(event) => set('bufferMinutes', Number(event.target.value))}>
                {[0, 5, 10, 15, 30, 60].map((value) => (
                  <option key={value} value={value}>
                    {value ? `${value} minutes` : 'None'}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Book at least</Label>
              <Select value={form.minNoticeHours} onChange={(event) => set('minNoticeHours', Number(event.target.value))}>
                {[0, 1, 2, 4, 12, 24, 48].map((value) => (
                  <option key={value} value={value}>
                    {value ? `${value} hours ahead` : 'Any time'}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Up to</Label>
              <Select value={form.maxDaysAhead} onChange={(event) => set('maxDaysAhead', Number(event.target.value))}>
                {[7, 14, 30, 60, 90].map((value) => (
                  <option key={value} value={value}>
                    {value} days ahead
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Questions on the form</Label>
            {form.questions.map((question, index) => (
              <div key={question.id} className="flex items-center gap-2">
                <Input
                  value={question.label}
                  onChange={(event) =>
                    set('questions', form.questions.map((item, at) => (at === index ? { ...item, label: event.target.value } : item)))
                  }
                />
                <label className="flex shrink-0 items-center gap-1 text-xs">
                  <Checkbox
                    checked={question.required}
                    onCheckedChange={(value) =>
                      set('questions', form.questions.map((item, at) => (at === index ? { ...item, required: Boolean(value) } : item)))
                    }
                  />
                  Required
                </label>
                <Button size="sm" variant="ghost" onClick={() => set('questions', form.questions.filter((_, at) => at !== index))}>
                  <Trash2 />
                </Button>
              </div>
            ))}
            {form.questions.length < 10 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => set('questions', [...form.questions, { id: `q${Date.now().toString(36)}`, label: '', required: false }])}
              >
                <Plus /> Add question
              </Button>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.createLead} onCheckedChange={(value) => set('createLead', value)} /> A guest nobody knows becomes an
            incoming lead
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.isActive} onCheckedChange={(value) => set('isActive', value)} /> Page is live
          </label>
          <FormError message={error} />
        </div>
        <DialogFooter className={cn(link && 'sm:justify-between')}>
          {link && (
            <Button
              variant="ghost"
              className="text-destructive"
              onClick={() => {
                if (!window.confirm('Delete this booking page? Meetings already booked stay.')) return;
                remove.mutate(link.id, { onSuccess: onClose });
              }}
            >
              <Trash2 /> Delete
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
